import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Lightbulb,
  ListChecks,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { useJsonLd } from "@/lib/useJsonLd";
import {
  RESPONSIBLE_NOTICE,
  TOOL_LINKS,
  lessonBySlug,
  neighborLessons,
  relatedLessons,
} from "@/data/playbook";
import { EVENTS, track } from "@/lib/analytics";
import { isCompleted, markCompleted, markIncomplete } from "@/lib/playbookProgress";
import { PLAYBOOK } from "@/constants/testIds";

const Paragraphs = ({ items }) =>
  items && items.length > 0
    ? items.map((t, i) => (
        <p key={i} className="text-slate-700 leading-relaxed">
          {t}
        </p>
      ))
    : null;

const BulletList = ({ items }) =>
  items && items.length > 0 ? (
    <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  ) : null;

const QuickQuiz = ({ quiz, lessonSlug }) => {
  const [answers, setAnswers] = useState({});

  const onAnswer = (qIdx, optIdx) => {
    if (answers[qIdx] !== undefined) return; // don't allow re-answer
    const q = quiz[qIdx];
    const correct = q.correct === optIdx;
    setAnswers((prev) => ({ ...prev, [qIdx]: { picked: optIdx, correct } }));
    track(EVENTS.PLAYBOOK_QUIZ_ANSWERED, {
      lesson_slug: lessonSlug,
      question_index: qIdx,
      correct,
    });
  };

  return (
    <ol className="space-y-6">
      {quiz.map((q, qIdx) => {
        const state = answers[qIdx];
        return (
          <li key={qIdx}>
            <p className="font-medium text-slate-900">
              {qIdx + 1}. {q.q}
            </p>
            <ul className="mt-2 space-y-2">
              {q.options.map((opt, optIdx) => {
                const isPicked = state && state.picked === optIdx;
                const isCorrectAnswer = q.correct === optIdx;
                const hasAnswered = state !== undefined;
                const highlight =
                  hasAnswered && isCorrectAnswer
                    ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                    : hasAnswered && isPicked && !state.correct
                      ? "border-red-500 bg-red-50 text-red-900"
                      : "border-slate-200 hover:border-slate-300 bg-white text-slate-800";
                return (
                  <li key={optIdx}>
                    <button
                      type="button"
                      onClick={() => onAnswer(qIdx, optIdx)}
                      disabled={hasAnswered}
                      data-testid={PLAYBOOK.quizOption(qIdx, optIdx)}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${highlight} disabled:cursor-not-allowed`}
                    >
                      <span className="inline-block w-6 font-mono-num text-slate-500">
                        {String.fromCharCode(65 + optIdx)}.
                      </span>
                      {opt}
                    </button>
                  </li>
                );
              })}
            </ul>
            {state && (
              <p
                data-testid={PLAYBOOK.quizResult(qIdx)}
                className={`mt-2 text-sm ${state.correct ? "text-emerald-700" : "text-red-700"}`}
              >
                {state.correct ? "Correct." : "Not quite."} {q.explanation}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};

const Section = ({ title, icon: IconCmp, children }) => (
  <section className="mt-8">
    <div className="flex items-center gap-2.5">
      {IconCmp && (
        <span
          aria-hidden="true"
          className="w-8 h-8 rounded-lg inline-flex items-center justify-center"
          style={{
            background: "rgba(16,185,129,0.14)",
            color: "#059669",
            border: "1px solid rgba(16,185,129,0.3)",
          }}
        >
          <IconCmp size={16} />
        </span>
      )}
      <h2 className="font-display text-xl md:text-2xl uppercase tracking-tight text-slate-900">
        {title}
      </h2>
    </div>
    <div className="mt-3 space-y-3">{children}</div>
  </section>
);

export const PlaybookLesson = () => {
  const { slug } = useParams();
  const lesson = lessonBySlug(slug);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!lesson) return;
    setDone(isCompleted(lesson.slug));
    track(EVENTS.PLAYBOOK_LESSON_OPENED, { lesson_slug: lesson.slug });
  }, [lesson && lesson.slug]);

  useSEO({
    title: lesson ? lesson.seoTitle : "Playbook | Parlay Assistant",
    description: lesson ? lesson.seoDescription : "",
    path: lesson ? `/playbook/${lesson.slug}` : "/playbook",
  });

  // ---- Structured data: Article + BreadcrumbList -----------------------
  // Populated only when a lesson resolves. Removed on unmount so other
  // routes don't inherit stale JSON-LD.
  const SITE = "https://parlayassistant.com";
  const lessonUrl = lesson ? `${SITE}/playbook/${lesson.slug}` : null;
  const articleJsonLd = lesson
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: lesson.title,
        description: lesson.seoDescription,
        mainEntityOfPage: { "@type": "WebPage", "@id": lessonUrl },
        url: lessonUrl,
        inLanguage: "en",
        isAccessibleForFree: true,
        author: { "@type": "Organization", name: "Parlay Assistant" },
        publisher: {
          "@type": "Organization",
          name: "Parlay Assistant",
          url: SITE,
          logo: {
            "@type": "ImageObject",
            url: `${SITE}/branding/pa-crest-v2.png`,
          },
        },
        image: [`${SITE}/branding/pa-og-image-v2.png`],
      }
    : null;
  const breadcrumbJsonLd = lesson
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Playbook", item: `${SITE}/playbook` },
          {
            "@type": "ListItem",
            position: 3,
            name: lesson.title,
            item: lessonUrl,
          },
        ],
      }
    : null;
  useJsonLd(lesson ? `article-${lesson.slug}` : null, articleJsonLd);
  useJsonLd(lesson ? `breadcrumb-${lesson.slug}` : null, breadcrumbJsonLd);

  if (!lesson) return <Navigate to="/playbook" replace />;

  const { prev, next } = neighborLessons(lesson.slug);
  const related = relatedLessons(lesson.slug);
  const tool = TOOL_LINKS[lesson.tool] || TOOL_LINKS.homepage;

  const onToolClick = () => {
    track(EVENTS.PLAYBOOK_RELATED_TOOL_CLICKED, {
      lesson_slug: lesson.slug,
      tool_key: lesson.tool,
      tool_href: tool.href,
    });
  };

  const onPrev = () => {
    if (prev) track(EVENTS.PLAYBOOK_PREVIOUS_LESSON_CLICKED, { from: lesson.slug, to: prev.slug });
  };
  const onNext = () => {
    if (next) track(EVENTS.PLAYBOOK_NEXT_LESSON_CLICKED, { from: lesson.slug, to: next.slug });
  };

  const toggleDone = () => {
    if (done) {
      markIncomplete(lesson.slug);
      setDone(false);
    } else {
      markCompleted(lesson.slug);
      setDone(true);
      track(EVENTS.PLAYBOOK_LESSON_COMPLETED, { lesson_slug: lesson.slug });
    }
  };

  return (
    <main
      data-testid={PLAYBOOK.lesson}
      className="max-w-4xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
    >
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="text-xs text-slate-400">
        <ol className="flex items-center gap-1.5">
          <li>
            <Link to="/" className="hover:text-white">Home</Link>
          </li>
          <li aria-hidden="true">/</li>
          <li>
            <Link to="/playbook" className="hover:text-white">Playbook</Link>
          </li>
          <li aria-hidden="true">/</li>
          <li className="text-slate-300 truncate max-w-[220px] md:max-w-none">
            {lesson.title}
          </li>
        </ol>
      </nav>

      <p className="mt-4 font-display text-emerald-400 text-xs tracking-[0.25em] uppercase">
        Playbook Lesson · {lesson.readingTime}
      </p>
      <h1 className="mt-2 font-display text-white text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-[1.05]">
        {lesson.title}
      </h1>
      <p className="mt-3 text-slate-300 text-base md:text-lg">{lesson.summary}</p>

      <article className="mt-8 pa-card p-6 md:p-10">
        <Section title="Explain It Like I'm in Fifth Grade" icon={Sparkles}>
          <Paragraphs items={lesson.body.explain} />
          <BulletList items={lesson.body.explainList} />
          <Paragraphs items={lesson.body.explainAfter} />
        </Section>

        <Section title="Simple Example">
          <Paragraphs items={lesson.body.example} />
          <BulletList items={lesson.body.exampleList} />
          <Paragraphs items={lesson.body.exampleAfter} />
        </Section>

        <Section title="Why It Matters">
          <Paragraphs items={lesson.body.whyItMatters} />
        </Section>

        <Section title="Biggest Beginner Mistake" icon={ShieldAlert}>
          <Paragraphs items={lesson.body.mistake} />
        </Section>

        <Section title="Playbook Tip" icon={Lightbulb}>
          <p className="text-slate-700 leading-relaxed">{lesson.body.tip}</p>
        </Section>

        <Section title="Important Rules to Check" icon={ListChecks}>
          {lesson.body.rulesIntro && (
            <p className="text-slate-700">{lesson.body.rulesIntro}</p>
          )}
          <BulletList items={lesson.body.rules} />
          {lesson.body.rulesOutro && (
            <p className="text-slate-700">{lesson.body.rulesOutro}</p>
          )}
        </Section>

        <Section title="Quick Quiz">
          <QuickQuiz quiz={lesson.quiz} lessonSlug={lesson.slug} />
        </Section>

        {/* Related tool */}
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 rounded-2xl border border-emerald-200 bg-emerald-50/60">
          <div>
            <p className="font-display text-slate-900 text-sm uppercase tracking-wide">
              Try it yourself
            </p>
            <p className="text-slate-600 text-sm mt-0.5">
              Put this lesson into practice with a free Parlay Assistant tool.
            </p>
          </div>
          <Link
            to={tool.href}
            onClick={onToolClick}
            data-testid={PLAYBOOK.relatedTool}
            className="pa-btn pa-btn-primary !text-sm !px-5"
          >
            {tool.label}
            <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        </div>

        {/* Mark complete */}
        <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-6">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2
              size={18}
              className={done ? "text-emerald-500" : "text-slate-300"}
            />
            <span className={done ? "text-emerald-700" : "text-slate-500"}>
              {done ? "Marked complete" : "Mark this lesson when you're done"}
            </span>
          </div>
          <button
            type="button"
            onClick={toggleDone}
            data-testid={PLAYBOOK.markComplete}
            aria-pressed={done}
            className={`pa-btn !text-sm ${done ? "pa-btn-ghost" : "pa-btn-primary"}`}
          >
            {done ? "Undo" : "Mark Complete"}
          </button>
        </div>
      </article>

      {/* Prev / Next */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {prev ? (
          <Link
            to={`/playbook/${prev.slug}`}
            onClick={onPrev}
            data-testid={PLAYBOOK.prev}
            className="pa-card-dark p-4 flex items-center gap-3 hover:border-emerald-500/60 transition-colors"
          >
            <ArrowLeft size={18} className="text-emerald-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-slate-400">Previous</p>
              <p className="font-display text-white uppercase tracking-tight truncate">
                {prev.title}
              </p>
            </div>
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link
            to={`/playbook/${next.slug}`}
            onClick={onNext}
            data-testid={PLAYBOOK.next}
            className="pa-card-dark p-4 flex items-center gap-3 sm:justify-end hover:border-emerald-500/60 transition-colors"
          >
            <div className="min-w-0 sm:text-right">
              <p className="text-xs uppercase tracking-wider text-slate-400">Next</p>
              <p className="font-display text-white uppercase tracking-tight truncate">
                {next.title}
              </p>
            </div>
            <ArrowRight size={18} className="text-emerald-400 shrink-0" />
          </Link>
        ) : (
          <span />
        )}
      </div>

      {/* Related lessons */}
      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-white text-xl uppercase tracking-tight">
            Keep Learning
          </h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/playbook/${r.slug}`}
                className="pa-card-dark p-4 hover:border-emerald-500/60 transition-colors"
              >
                <p className="text-xs text-emerald-400 uppercase tracking-wider">
                  {r.readingTime} read
                </p>
                <p className="mt-1 font-display text-white text-lg uppercase tracking-tight leading-tight">
                  {r.title}
                </p>
                <p className="mt-1 text-slate-400 text-sm">{r.summary}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <p className="mt-10 mb-4 text-slate-400 text-xs md:text-sm leading-relaxed">
        {RESPONSIBLE_NOTICE}
      </p>
    </main>
  );
};

export default PlaybookLesson;
